import React, { useState } from 'react';
import { Screen } from '../App';
import { User } from '../storage';
import * as authApi from '../authApi';

interface Props { navigate: (s: Screen) => void; user: User | null; }

const LABELS = ['', 'Not helpful', 'Could be better', "It's okay", 'Helpful', 'Love it'];

export default function Feedback({ navigate, user }: Props) {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [sent, setSent] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setErr('');
    if (rating < 1 || rating > 5) { setErr('Please choose a rating from 1 to 5.'); return; }
    setBusy(true);
    try {
      // One-time trial-end nudge (if shown) is satisfied once feedback is sent.
      try { localStorage.setItem('dms_feedback_prompted', '1'); } catch { /* ignore */ }
      await authApi.sendFeedback({ rating, comment: comment.trim() });
      setSent(true);
    } catch (e: any) {
      setErr(e?.message || "We couldn't send your feedback just now. Please try again.");
    } finally { setBusy(false); }
  };

  const active = hover || rating;

  return (
    <div>
      <div className="top-bar">
        <button onClick={() => navigate('dashboard')} style={{ background: 'none', color: 'var(--teal)', display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><polyline points="15,18 9,12 15,6" /></svg>Back
        </button>
        <span className="top-bar-logo" style={{ fontSize: 15 }}>Send feedback</span>
        <div style={{ width: 60 }} />
      </div>

      <div className="page-content" style={{ paddingTop: 32 }}>
        {sent ? (
          <div className="card" style={{ textAlign: 'center', padding: '32px 20px' }}>
            <p style={{ fontSize: 36, marginBottom: 12 }}>💛</p>
            <h2 style={{ marginBottom: 8 }}>Thank you</h2>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 20 }}>
              Your feedback went straight to the team. It genuinely shapes what we build next.
            </p>
            <button onClick={() => navigate('dashboard')} className="btn-primary">Back to dashboard</button>
          </div>
        ) : (
          <>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
              How is Decode My Student working for you? Your rating and suggestions go straight to the team.
            </p>
            <div className="card">
              <form onSubmit={submit}>
                <div className="form-group">
                  <label>Your rating</label>
                  <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    {[1, 2, 3, 4, 5].map(n => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setRating(n)}
                        onMouseEnter={() => setHover(n)}
                        onMouseLeave={() => setHover(0)}
                        aria-label={`${n} star${n > 1 ? 's' : ''}`}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 34, lineHeight: 1, padding: 0, color: n <= active ? 'var(--amber)' : '#d8d2c8' }}
                      >
                        ★
                      </button>
                    ))}
                  </div>
                  {active > 0 && <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 6 }}>{LABELS[active]}</p>}
                </div>
                <div className="form-group">
                  <label>Suggestions (optional)</label>
                  <textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="What's working? What would make this better for you?" rows={5} style={{ resize: 'vertical' }} />
                </div>
                {err && <div style={{ background: 'var(--coral-light)', color: 'var(--coral)', padding: '10px 14px', borderRadius: 8, fontSize: 14, marginBottom: 12 }}>{err}</div>}
                <button type="submit" className="btn-primary" disabled={busy || rating < 1}>{busy ? 'Sending…' : 'Send feedback'}</button>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
